import React, { useEffect, useMemo, useState } from 'react';
import { Card, Row, Col, Form, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Select from 'react-select';
import PageHeader from 'components/common/PageHeader';
import FullPageSpinner from 'components/common/full-page-spinner';
import {
  getDepartments,
  getFinancialYears,
  getDepartmentSpendingSummaryFullYear
} from 'services/department-spending/departmentSpendingService';
import DepartmentSpendingBudgetChart from './DepartmentSpendingBudgetChart';
import DepartmentSpendingCurrentMonthGauge from './DepartmentSpendingCurrentMonthGauge';

const DepartmentSpendingDashboard = () => {
  const navigate = useNavigate();

  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [financialYearOptions, setFinancialYearOptions] = useState([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);

  const [department, setDepartment] = useState(null);
  const [financialYear, setFinancialYear] = useState(null);

  const [summary, setSummary] = useState(null);
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);

  useEffect(() => {
    const loadOptions = async () => {
      setIsLoadingOptions(true);
      try {
        const [departmentsRes, financialYearsRes] = await Promise.all([
          getDepartments(),
          getFinancialYears()
        ]);
        if (departmentsRes?.success === false) toast.error(departmentsRes.message);
        if (financialYearsRes?.success === false)
          toast.error(financialYearsRes.message);
        setDepartmentOptions(departmentsRes?.data || []);
        setFinancialYearOptions(financialYearsRes?.data || []);
      } catch (error) {
        console.log('AXIOS ERROR', error);
      } finally {
        setIsLoadingOptions(false);
      }
    };
    loadOptions();
  }, []);

  useEffect(() => {
    if (!department || !financialYear) {
      setSummary(null);
      return;
    }
    const loadSummary = async () => {
      setIsLoadingSummary(true);
      try {
        const res = await getDepartmentSpendingSummaryFullYear(
          department.value,
          financialYear.value
        );
        if (res?.success === false) toast.error(res.message);
        setSummary(res?.data || null);
      } catch (error) {
        console.log('AXIOS ERROR', error);
        setSummary(null);
      } finally {
        setIsLoadingSummary(false);
      }
    };
    loadSummary();
  }, [department, financialYear]);

  const departmentSelectOptions = useMemo(
    () =>
      departmentOptions.map(d => ({
        value: d.code,
        label: d.name
      })),
    [departmentOptions]
  );

  const financialYearSelectOptions = useMemo(
    () =>
      financialYearOptions.map(fy => ({
        value: fy.id,
        label: fy.label
      })),
    [financialYearOptions]
  );

  const hasSelection = !!department && !!financialYear;

  const goToDetail = () => {
    if (!hasSelection) return;
    navigate(
      `/department-spending?department=${encodeURIComponent(
        department.value
      )}&financialYearId=${encodeURIComponent(financialYear.value)}`
    );
  };

  return (
    <>
      <PageHeader
        title="Department Spending Dashboard"
        description="Profit and loss vs budget for the full financial year, and current month spend by department."
        className="mb-3"
      />

      <Card>
        <Card.Header>
          <Row className="g-3">
            <Col xs={12} sm={6} md={4} lg={3}>
              <Form.Group controlId="department-spending-dashboard-department">
                <Form.Label>Department</Form.Label>
                <Select
                  inputId="department-spending-dashboard-department"
                  isClearable
                  isLoading={isLoadingOptions}
                  options={departmentSelectOptions}
                  value={department}
                  onChange={setDepartment}
                  placeholder="Select department..."
                  classNamePrefix="react-select"
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                />
              </Form.Group>
            </Col>

            <Col xs={12} sm={6} md={4} lg={3}>
              <Form.Group controlId="department-spending-dashboard-financial-year">
                <Form.Label>Budget Year To Date</Form.Label>
                <Select
                  inputId="department-spending-dashboard-financial-year"
                  isClearable
                  isLoading={isLoadingOptions}
                  options={financialYearSelectOptions}
                  value={financialYear}
                  onChange={setFinancialYear}
                  placeholder="Select financial year..."
                  classNamePrefix="react-select"
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Header>

        <Card.Body>
          {hasSelection ? (
            <>
              <Row>
                <Col md={8}>
                  <DepartmentSpendingBudgetChart summary={summary} />
                </Col>
                <Col md={4}>
                  <DepartmentSpendingCurrentMonthGauge summary={summary} />
                </Col>
              </Row>
              <Row className="mt-3">
                <Col className="text-end">
                  <Button variant="falcon-default" onClick={goToDetail}>
                    Detail
                  </Button>
                </Col>
              </Row>
            </>
          ) : (
            <div className="text-center text-muted p-5">
              Select a department and a financial year to view the dashboard.
            </div>
          )}
        </Card.Body>
      </Card>

      <FullPageSpinner
        show={isLoadingOptions || isLoadingSummary}
        quantity={3}
        variant="primary"
      />
    </>
  );
};

export default DepartmentSpendingDashboard;
